/**
 * Guardas de rota do web (docs/port/04 §5.2).
 *
 * WEB: não existem no nativo — lá só se chega às telas internas a partir de uma sessão
 * válida. No browser a barra de endereço, o F5 e os deep links (notificação, PWA) abrem
 * QUALQUER rota direto, então cada área confere a sessão antes de montar a tela.
 * Enquanto a sessão hidrata do IndexedDB, mostra o splash estático (logo sobre #294956),
 * o mesmo visual do boot.
 */
import splashNova from "@/assets/images/splash_nova.png";
import { useAuth } from "@/contexts/AuthContext";
import { usePrestadorAuth } from "@/contexts/PrestadorAuthContext";
import { estaSaindoDoApp, useLocalSearchParams } from "@/navigation";
import { precarregarTelas } from "./precarregarTelas";
import { type ReactNode, useEffect, useState } from "react";
import { Navigate, Outlet } from "react-router";
import s from "./index.module.css";

/**
 * Carência antes de redirecionar por falta de sessão: o login grava a sessão e navega em
 * seguida; se a navegação chegar um instante antes do estado do contexto, a guarda não
 * pode jogar o usuário de volta para o splash.
 */
const CARENCIA_SEM_SESSAO_MS = 400;

export function SplashEstatico() {
  return (
    <div className={s.container} aria-busy="true">
      <img src={splashNova} alt="" className={s.logo} style={{ width: "50vw", height: "50vw" }} />
    </div>
  );
}

function useSemSessaoConfirmado(semSessao: boolean): boolean {
  const [confirmado, setConfirmado] = useState(false);
  useEffect(() => {
    if (!semSessao) {
      setConfirmado(false);
      return;
    }
    const t = setTimeout(() => setConfirmado(true), CARENCIA_SEM_SESSAO_MS);
    return () => clearTimeout(t);
  }, [semSessao]);
  // Saindo do app ("Sair" -> login do TirvuAdm): a sessão sumiu de propósito e o documento
  // novo está carregando; redirecionar agora cancelaria a saída (ver navigation/router.ts).
  return semSessao && confirmado && !estaSaindoDoApp();
}

/** Área do colaborador (CLT): abas, câmera do ponto, biometria, atualização cadastral... */
export function RequireColaborador({ children }: { children?: ReactNode }) {
  const { isAuthenticated, isHydrated } = useAuth();
  const semSessao = isHydrated && !isAuthenticated;
  const redirecionar = useSemSessaoConfirmado(semSessao);
  // Sessão aberta: baixa o código das outras telas no ocioso (troca de tela sem "vazio").
  useEffect(() => {
    if (isAuthenticated) precarregarTelas();
  }, [isAuthenticated]);
  if (!isHydrated) return <SplashEstatico />;
  if (redirecionar) return <Navigate to="/" replace />;
  if (semSessao) return <SplashEstatico />;
  return <>{children ?? <Outlet />}</>;
}

/** Área do prestador: diária, câmera da diária, meus dados. */
export function RequirePrestador({ children }: { children?: ReactNode }) {
  const { token, isHydrated } = usePrestadorAuth();
  const semSessao = isHydrated && !token;
  const redirecionar = useSemSessaoConfirmado(semSessao);
  if (!isHydrated) return <SplashEstatico />;
  if (redirecionar) return <Navigate to="/onboarding-prestador" replace />;
  if (semSessao) return <SplashEstatico />;
  return <>{children ?? <Outlet />}</>;
}

/**
 * /suporte-chat: o chat abre com a identidade do prestador (`?perfil=prestador`) ou do
 * colaborador. Exige a sessão correspondente.
 */
export function RequireSuporteIdentity({ children }: { children?: ReactNode }) {
  const { perfil } = useLocalSearchParams<{ perfil?: string }>();
  const clt = useAuth();
  const prestador = usePrestadorAuth();
  const ehPrestador = perfil === "prestador";
  const hidratado = ehPrestador ? prestador.isHydrated : clt.isHydrated;
  const temSessao = ehPrestador ? Boolean(prestador.token) : clt.isAuthenticated;
  const semSessao = hidratado && !temSessao;
  const redirecionar = useSemSessaoConfirmado(semSessao);
  if (!hidratado) return <SplashEstatico />;
  if (redirecionar) return <Navigate to="/" replace />;
  if (semSessao) return <SplashEstatico />;
  return <>{children ?? <Outlet />}</>;
}
